import compose from 'lodash/fp/compose';
import { getIn } from 'seamless-immutable';
import sortBy from 'lodash/sortBy';
import { formatPrice } from '@catalogo/core-helpers/price';
import { getFreightErrorMessage } from '@catalogo/ui-americanas-mobile-cep-field/src/helpers/freight';

export const fmtVipProduct = vip => {
  const isActive = getIn(vip, ['buyTogetherVIP', 'active']);
  const sku = getIn(vip, ['buyTogetherVIP', 'sku']);
  const storeId = getIn(vip, ['buyTogetherVIP', 'sellerId']);

  return isActive ? [{ sku, storeId }] : [];
};

export const fmtFreightProducts = (currentSku, vip, quantity) => {
  const sku = getIn(currentSku, ['id'], '').toString();
  const offer = getIn(currentSku, ['offers', 'result', 0]);
  const condition = getIn(offer, ['condition']);
  const storeId = getIn(offer, ['seller', 'id']);
  const mainProduct = { sku, condition, storeId, quantity };
  const vipProduct = fmtVipProduct(vip);

  return [mainProduct].concat(vipProduct);
};

export const formattedPrice = price => {
  if (price !== null && price >= 0) {
    return price === 0 ? 'grátis' : `R$ ${formatPrice(price)}`;
  }

  return undefined;
};

export const shouldCallFreight = (page, feature, click) => {
  const features = getIn(page, ['components', 'page-config', 'features'], '').split(', ');
  const isFeatureOn = features.includes(feature);

  return isFeatureOn || click;
};

export const formatDateRange = text => {
  if (!text) {
    return { dateText: '', dateRange: '' };
  }
  const [dateText, dateRange] = text.split(/(\d.+)/, 2);

  return { dateText, dateRange };
};

export const isPrimeOption = id => (id?.indexOf('PRIME') === -1 ? false : true);

export const fmtOption = option => {
  const text = option?.eta?.text;

  const { dateText, dateRange } = formatDateRange(text);
  const price = getIn(option, ['price']);
  const listPrice = getIn(option, ['priceWithoutPromotion']);
  const id = getIn(option, ['optionId']);
  const details = getIn(option, ['products', 0, 'details']);

  return {
    dateText,
    dateRange,
    eta: option.eta,
    prime: isPrimeOption(id),
    listPrice: formattedPrice(listPrice),
    price: formattedPrice(price),
    id,
    details,
  };
};

export const getFastestFreight = options => {
  const orderedOptions = sortBy(options, option => getIn(option, ['eta', 'businessDays']));

  return orderedOptions[0];
};

export const getPickupOptionFreight = freightOptions => {
  const freightTypes = ['PICK_UP_IN_STORE_SCHEDULED', 'PICKUP_STORE', 'PICK_UP_IN_STORE', 'LASA', 'LASAPRIME'];

  const options = freightOptions.filter(option => freightTypes.includes(option.optionId));

  if (options.length < 1) {
    return null;
  }

  return getFastestFreight(options);
};

export const getCheapestFreight = options =>
  options.sort((acc, value) => {
    if (acc.price > value.price) {
      return 1;
    }
    if (acc.price < value.price) {
      return -1;
    }
    return 0;
  })[0];

export const getBestFreightOptions = options => {
  if (options.length < 1) {
    return options;
  }

  const fastest = getFastestFreight(options);
  const cheapest = getCheapestFreight(options);

  if (fastest.optionId === cheapest.optionId) {
    return [fastest];
  }

  return [fastest, cheapest];
};

export const addPickupOption = freightOptions => options => {
  const pickupOption = getPickupOptionFreight(freightOptions);
  const fastestOption = getIn(options, [0]);

  if (
    pickupOption &&
    getIn(pickupOption, ['eta', 'businessDays'], Infinity) <= getIn(fastestOption, ['eta', 'businessDays'])
  ) {
    return (options ?? []).concat([pickupOption]);
  }

  return options;
};

export const formatFreightOptions = (freightOptions, isPrime) => {
  if (freightOptions.length <= 1) {
    return freightOptions;
  }

  return compose(
    addPickupOption(freightOptions),
    getBestFreightOptions,
    filterPrimeOptions(isPrime),
    excludesLasaOption
  )(freightOptions);
};

export const excludesLasaOption = options => {
  const pickupIds = [
    'PICK_UP_IN_STORE_SCHEDULED',
    'PICKUP_STORE',
    'PICK_UP_IN_STORE',
    'LASA',
    'LASAPRIME',
    'LOCKER',
    'LOCKERPRIME',
  ];

  return options.filter(option => !pickupIds.includes(option.optionId));
};

export const filterPrimeOptions = isPrime => options => {
  if (isPrime) {
    return options;
  }

  const filtered = options.filter(option => option.optionId.indexOf('PRIME') === -1);

  return filtered.length < 1 ? options : filtered;
};

export const getBusinessDaysFromText = option => {
  if (option?.eta?.text?.includes('amanhã')) {
    return 1;
  }

  if (option?.eta?.text?.includes('hoje')) {
    return 0;
  }

  return option?.eta?.businessDays;
};

const getStoreDeliveryType = store => {
  const storeSFS = store?.selectedStore?.deliveryDaysSFS;
  const storePNLH = store?.selectedStore?.deliveryDaysPNLH;

  return {
    storeSFS: Number.isInteger(storeSFS) ? storeSFS : Infinity,
    storePNLH: Number.isInteger(storePNLH) ? storePNLH : Infinity,
  };
};

export const shouldShowLasaOffer = (options, currentStore) => {
  const { storeSFS, storePNLH } = getStoreDeliveryType(currentStore);

  const express = options.find(option =>
    ['EXPRESSPRIME', 'EXPRESS', 'CONVENTIONALPRIME', 'CONVENTIONAL'].includes(option.id)
  );
  const pickup = options.find(option => ['PICK_UP_IN_STORE', 'LASAPRIME', 'LASA'].includes(option.id));
  const expressBD = getBusinessDaysFromText(express);
  const pickupBD = getBusinessDaysFromText(pickup);

  return expressBD > storeSFS || pickupBD > storePNLH;
};

export const fmtFreight = (freight, hasPrime, currentStore) => {
  const options = getIn(freight, ['options']) || [];
  const filteredOptions = formatFreightOptions(options, hasPrime).map(option => fmtOption(option));
  const hasO2O = filteredOptions.some(({ id }) =>
    ['PICK_UP_IN_STORE_SCHEDULED', 'PICK_UP_IN_STORE', 'LASAPRIME', 'LASA', 'LOCKER', 'LOCKERPRIME'].includes(id)
  );
  const fastDelivery = shouldShowLasaOffer(filteredOptions, currentStore);

  return {
    options: filteredOptions,
    cep: getIn(freight, ['cep']),
    hasMoreOptions: filteredOptions.length < options.length,
    hasO2O,
    fastDelivery,
  };
};

export const getWarningMessage = data => {
  const warningCode = getIn(data, ['freight', 'warnings', '0', 'code']);

  if (warningCode) {
    return getFreightErrorMessage(warningCode).message;
  }
};

export const getLasaOffer = (currentSku, product) => {
  const skuId = getIn(currentSku, ['id'], '').toString();
  const offers = getIn(product, ['lasa', 'result']) || [];
  const offer = offers.find(({ sku }) => sku === skuId);

  return offer;
};

export const fmtFreightData = ({ data, currentSku, product, error, hasPrime, currentStore }) => {
  const freight = fmtFreight(getIn(data, ['freight']), hasPrime, currentStore);
  const freightError = error || getIn(data, ['freight', 'error']);
  const preSale = { active: getIn(product, ['isPreSale']), launchingAt: getIn(product, ['launchingAt']) };
  const unified = getIn(currentSku, ['offers', 'result', '0', 'isUnified']);
  const international = getIn(currentSku, ['offers', 'result', '0', 'currency']) === 'USD';
  const warningMessage = getWarningMessage(data);
  return { freight, freightError, preSale, unified, international, warningMessage };
};

export function getZipCodeMain(customer) {
  const addresses = customer?.customer?.addresses;
  return addresses?.find(addresse => addresse.main).zipCode;
}

export const hasOverride = freightOptions => {
  const hasReceiveIn3Hours = freightOptions?.some(opt => opt?.eta?.text.toLowerCase() === 'receba em 3 horas');
  return hasReceiveIn3Hours ? null : 'OUT';
};

export const getTimerExperimentName = brand => {
  const experiments = {
    suba: 'CAT_024_TIMER_SUBA',
    shop: 'CAT_025_TIMER_SHOP',
  };

  return experiments[brand];
};

export const fmtFreightDataForMetrics = (data, labelType) => ({
  ...data,
  freight: {
    ...data?.freight,
    labelType,
  },
});

export const handleDispatchMetrics = (dispatchMetrics, labelType) => data => {
  dispatchMetrics('freight:event', fmtFreightDataForMetrics(data, labelType));
};
